/**
 * @author Antoine Laviron
 * @date 2024-09-03
 *
 * @description
 * This component provides a full-screen, two-panel playground for embedding
 * and rendering any Salesforce Analytics asset (Dashboard, Visualization, or Metric).
 * It features a code editor on the left and a live preview pane on the right.
 */

import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";


function Playground() {
  const navigate = useNavigate();
  const [embedCode, setEmbedCode] = useState("");
  const [error, setError] = useState("");
  const containerRef = useRef(null);

  /**
   * Embeds an asset using the Analytics SDK.
   * @param {string} assetType - The type of asset ('AnalyticsDashboard', 'AnalyticsVisualization', 'AnalyticsMetric').
   * @param {string} assetId - The API name or ID of the asset.
   * @param {Array} filters - An array of filter objects.
   */
  const embedAsset = async (assetType, assetId, filters) => {
    if (!containerRef.current) return;
    containerRef.current.innerHTML = ""; // Clear previous content

    try {
      const authCredential = localStorage.getItem("frontdoor_url");
      const orgUrl = localStorage.getItem("instance_url");

      if (!authCredential || !orgUrl) {
        navigate("/");
        return;
      }

      const {
        initializeAnalyticsSdk,
        AnalyticsDashboard,
        AnalyticsVisualization,
        AnalyticsMetric,
      } = await import("@salesforce/analytics-embedding-sdk");

      const config = { authCredential, orgUrl };
      await initializeAnalyticsSdk(config);

      let asset;
      const options = {
        parentIdOrElement: containerRef.current,
        idOrApiName: assetId,
      };

      switch (assetType) {
        case "AnalyticsDashboard":
          asset = new AnalyticsDashboard(options);
          if (filters && filters.length > 0) {
            asset.filters = filters;
          }
          break;
        case "AnalyticsVisualization":
          asset = new AnalyticsVisualization(options);
          break;
        case "AnalyticsMetric":
          asset = new AnalyticsMetric(options);
          break;
        default:
          throw new Error(`Unsupported asset type: ${assetType}`);
      }
      asset.render();
    } catch (err) {
      console.error("Failed to embed asset:", err);
      setError("An error occurred while embedding the asset. Check the console for details.");
    }
  };

  /**
   * Parses the user-provided embed code to extract parameters and render the asset.
   */
  const handleRender = () => {
    setError(""); // Reset previous errors.

    if (!embedCode.trim()) {
        setError("Embed code cannot be empty.");
        return;
    }

    try {
      const typeMatch = embedCode.match(
        /new\s+(AnalyticsDashboard|AnalyticsVisualization|AnalyticsMetric)/
      );
      const assetType = typeMatch?.[1];
      if (!assetType) throw new Error("Could not determine the asset type from the code.");

      const idMatch = embedCode.match(/idOrApiName\s*:\s*['"]([^'"]+)['"]/);
      const assetId = idMatch?.[1];
      if (!assetId) throw new Error("Could not extract 'idOrApiName'.");

      const filtersMatch = embedCode.match(
        /(?:dashboard|viz)\.filters\s*=\s*(\[[\s\S]*?\])\s*[\n;]/
      );
      let filters = [];
      if (filtersMatch?.[1]) {
        try {
          // A controlled use of eval where the input is constrained by regex
          filters = eval(`(${filtersMatch[1]})`);
          if (!Array.isArray(filters)) {
            throw new Error("Parsed filters are not an array.");
          }
        } catch (err) {
          throw new Error("Failed to parse the filters array from the code.");
        }
      }

      embedAsset(assetType, assetId, filters);
    } catch (err) {
      console.error("Parsing error:", err);
      setError(`Failed to parse the embed code: ${err.message}`);
    }
  };
  
  return (
    <div className="playground-full-page">
      <div className="playground-editor">
        <div className="editor-header">
            <button onClick={() => navigate(-1)} className="playground-back-button">
                ← Back
            </button>
            <button onClick={handleRender} className="run-button">
                ▶ Run
            </button>
        </div>
        <div className="playground-code-editor-wrapper">
            <textarea
                className="embed-code-textarea-playground"
                value={embedCode}
                onChange={(e) => setEmbedCode(e.target.value)}
                placeholder={`// Paste your Salesforce Analytics embed code here...`}
                spellCheck="false"
                autoCapitalize="off"
                autoComplete="off"
                autoCorrect="off"
            />
        </div>

        {error && <p className="error-message">{error}</p>}
      </div>
      <div className="playground-preview" ref={containerRef}>
        <div className="playground-placeholder">
            <h3>Preview Area</h3>
            <p>Click "Run" to render your embedded asset here.</p>
        </div>
      </div>
    </div>
  );
}

export default Playground;